import AppComponent from '../base/app-component.ts';

interface OperatoreItem {
  idFenice: number;
  nome: string;
  cognome: string;
  territorio: string | null;
}

/** "Cognome Nome (530)": il codice Fenice è quello che gli operatori scrivono sulle deleghe */
const nomeConCodice = (op: OperatoreItem): string => `${op.cognome} ${op.nome} (${op.idFenice})`;

/**
 * wc-operatore-combobox — Selezione operatore con due modalità:
 *
 * 1. SELECT (default): dropdown con operatori Bergamo dalla cache
 * 2. SEARCH: input testo con ricerca remota cross-territorio su Fenice
 *
 * Emette CustomEvent 'operatore-change' con detail: { idFenice, cognome, nome, territorio } | null.
 *
 * Attributi:
 *   value   (opzionale) ID Fenice preselezionato
 *   label   (opzionale) label da mostrare se preselezionato
 */
export class OperatoreCombobox extends AppComponent {
  static override componentName = 'wc-operatore-combobox';
  protected override get templateId() { return 'operatore-combobox'; }

  private operatoriLocali: OperatoreItem[] = [];
  private selected: OperatoreItem | null = null;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  protected override afterRender(): void {
    this.loadLocali();
    this.setupModeSwitch();
    this.setupSelect();
    this.setupSearch();
    this.setupClear();

    // Precompilazione
    const preId = this.attributeNumber('value');
    const preLabel = this.attributeString('label');
    if (preId && preLabel) this.preseleziona(preId, preLabel);
  }

  /** Mostra come già scelto un operatore salvato (label già composta dal server).
   *  Gli attributi coprono il caso in cui il componente non sia ancora renderizzato. */
  preseleziona(idFenice: number, label: string): void {
    this.setAttribute('value', String(idFenice));
    this.setAttribute('label', label);
    this.confirmSelection({ idFenice, nome: '', cognome: label, territorio: null }, label);
  }

  // --- Getter pubblici ---

  get value(): number | null {
    return this.selected?.idFenice ?? null;
  }

  get operatoreNome(): string | null {
    if (!this.selected) return null;
    return this.selected.nome ? nomeConCodice(this.selected) : this.selected.cognome;
  }

  // --- Caricamento operatori Bergamo ---

  private async loadLocali(): Promise<void> {
    const result = await this.api.operatori.lista();
    if (!result.ok) return;
    this.operatoriLocali = result.data.operatori;
    this.populateSelect();
  }

  private populateSelect(): void {
    const select = this.$('[data-operatore-select]') as HTMLSelectElement | null;
    if (!select) return;
    while (select.options.length > 1) select.remove(1);
    for (const op of this.operatoriLocali) {
      const option = document.createElement('option');
      option.value = String(op.idFenice);
      option.textContent = nomeConCodice(op);
      select.appendChild(option);
    }
  }

  // --- Switch modalità ---

  private setupModeSwitch(): void {
    this.delegate.on('click', '[data-action="mode-search"]', () => this.showMode('search'));
    this.delegate.on('click', '[data-action="mode-select"]', () => this.showMode('select'));
  }

  private showMode(mode: 'select' | 'search'): void {
    const selectDiv = this.$('[data-mode="select"]');
    const searchDiv = this.$('[data-mode="search"]');
    if (!selectDiv || !searchDiv) return;

    selectDiv.classList.toggle('d-none', mode !== 'select');
    searchDiv.classList.toggle('d-none', mode !== 'search');

    if (mode === 'search') {
      const input = this.$('[data-operatore-input]') as HTMLInputElement | null;
      if (input) { input.value = ''; input.focus(); }
    }
  }

  // --- Modalità SELECT ---

  private setupSelect(): void {
    const select = this.$('[data-operatore-select]') as HTMLSelectElement | null;
    if (!select) return;

    this.listen(select, 'change', () => {
      const id = Number(select.value);
      if (!id) return;
      const op = this.operatoriLocali.find(o => o.idFenice === id);
      if (op) this.confirmSelection(op, nomeConCodice(op));
    });
  }

  // --- Modalità SEARCH ---

  private setupSearch(): void {
    const input = this.$('[data-operatore-input]') as HTMLInputElement | null;
    if (!input) return;

    this.listen(input, 'input', () => {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      const q = input.value.trim();
      if (!/^\d+$/.test(q) && q.length < 3) {
        this.hideDropdown();
        return;
      }
      this.searchTimeout = setTimeout(() => this.doSearch(q), 300);
    });

    this.listen(input, 'keydown', (e: Event) => {
      const { key } = e as KeyboardEvent;
      if (key === 'Escape') this.hideDropdown();
      // Dentro un form, Enter invierebbe il form con la ricerca a metà
      if (key === 'Enter') e.preventDefault();
    });
  }

  private async doSearch(q: string): Promise<void> {
    const result = await this.api.operatori.cerca(q);
    if (!result.ok) return;

    const { locali, remoti } = result.data;
    const all = [...locali, ...remoti];
    this.showDropdown(all);
  }

  private showDropdown(items: OperatoreItem[]): void {
    const dropdown = this.$('[data-operatore-dropdown]');
    if (!dropdown) return;

    if (items.length === 0) {
      dropdown.innerHTML = '<div class="dropdown-item text-muted">Nessun risultato</div>';
      dropdown.classList.add('show');
      return;
    }

    dropdown.innerHTML = items.map(op => {
      const territorio = op.territorio ? ` <small class="text-muted">(${op.territorio})</small>` : '';
      return `<a class="dropdown-item" href="#" data-op-id="${op.idFenice}">${nomeConCodice(op)}${territorio}</a>`;
    }).join('');
    dropdown.classList.add('show');

    this.delegate.offAll('click', '[data-op-id]');
    this.delegate.on('click', '[data-op-id]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-op-id]') as HTMLElement;
      const id = Number(el.dataset.opId);
      const op = items.find(o => o.idFenice === id);
      if (op) this.selectFromSearch(op);
    });
  }

  private hideDropdown(): void {
    const dropdown = this.$('[data-operatore-dropdown]');
    if (dropdown) dropdown.classList.remove('show');
  }

  private async selectFromSearch(op: OperatoreItem): Promise<void> {
    this.hideDropdown();
    // Salva in cache se non è già locale
    if (!this.operatoriLocali.some(o => o.idFenice === op.idFenice)) {
      await this.api.operatori.salvaCache(op);
      this.operatoriLocali.push(op);
      this.populateSelect();
    }
    const label = `${nomeConCodice(op)}${op.territorio ? ` - ${op.territorio}` : ''}`;
    this.confirmSelection(op, label);
  }

  // --- Conferma / Clear ---

  private confirmSelection(op: OperatoreItem, label: string): void {
    this.selected = op;

    const hidden = this.$('[data-operatore-value]') as HTMLInputElement | null;
    if (hidden) hidden.value = String(op.idFenice);

    // Nascondi entrambe le modalità, mostra conferma
    this.$('[data-mode="select"]')?.classList.add('d-none');
    this.$('[data-mode="search"]')?.classList.add('d-none');
    const confirmed = this.$('[data-operatore-confirmed]');
    if (confirmed) {
      confirmed.classList.remove('d-none');
      const labelEl = confirmed.querySelector('[data-operatore-label]');
      if (labelEl) labelEl.textContent = label;
    }

    this.dispatch('operatore-change', { idFenice: op.idFenice, nome: op.nome, cognome: op.cognome, territorio: op.territorio });
  }

  private setupClear(): void {
    this.delegate.on('click', '[data-action="clear-operatore"]', (e: Event) => {
      e.preventDefault();
      this.clearSelection();
    });
  }

  clearSelection(): void {
    this.selected = null;

    const hidden = this.$('[data-operatore-value]') as HTMLInputElement | null;
    if (hidden) hidden.value = '';

    // Reset select
    const select = this.$('[data-operatore-select]') as HTMLSelectElement | null;
    if (select) select.value = '';

    // Torna a modalità select
    this.$('[data-operatore-confirmed]')?.classList.add('d-none');
    this.showMode('select');

    this.dispatch('operatore-change', null);
  }
}
